import { Box, CloseButton, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { ListPageHeader } from "../../../common/components/ListPageHeader";
import { uniqBy } from "lodash";
import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  ListContractTasksResponseDto,
  PersonDto,
  SavedReportListDto,
  useGetPersonsByIdsQuery,
  useListContractTasksForOrganizationQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { formatNumber } from "../../../common/formatting/formatNumber";
import { ListLoadError } from "../../../common/ListLoadError";
import { Paging } from "../../../common/Paging";
import { useSkeletons } from "../../../common/useSkeletons";
import { MultiSelectOption } from "../view-all/filtering/MultiSelectFilter";
import { TaskListDownloadMenu } from "./TaskListDownloadMenu";
import { changedSinceLabel, describeTaskFilters, dueBeforeLabel } from "./taskListExport";
import { TasksTable } from "./TasksTable";
import { TaskFilterBar } from "./filtering/TaskFilterBar";
import {
  NO_TASK_FILTERS,
  type TaskListFilters,
  taskFiltersFromAddress,
  type UnknownTaskFilterValue,
  unknownTaskFilterValues,
  withTaskFilters,
} from "./taskListAddress";
import { useContractTitles } from "./useContractTitles";
import { useListReports } from "../../../common/saved-reports/useListReports";

export const PAGE_SIZE = 50;

interface TaskPaging {
  filterKey: string;
  positions: (number | undefined)[];
  page: number;
}

export const ContractTasks = () => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = taskFiltersFromAddress(searchParams);
  const unknownValues = unknownTaskFilterValues(searchParams);
  const updateFilters = (change: Partial<TaskListFilters>) =>
    setSearchParams((prev) => withTaskFilters(prev, { ...taskFiltersFromAddress(prev), ...change }), { replace: true });

  const [chosenPersons, setChosenPersons] = useState<PersonDto[]>(() => personFromAddress(searchParams));
  const [chosenContracts, setChosenContracts] = useState<MultiSelectOption[]>([]);
  const { currentData: personsInAddress, isError: personsFailed } = useGetPersonsByIdsQuery(
    { personIds: filters.personIds },
    { skip: filters.personIds.length === 0 }
  );
  const contractTitles = useContractTitles(filters.contractIds);

  const personsSettled = personsInAddress !== undefined || personsFailed;
  const selectedResponsiblePersons: PersonDto[] = filters.personIds.map(
    (id) =>
      chosenPersons.find((person) => person.id === id) ??
      personsInAddress?.persons.find((person) => person.id === id) ?? {
        id,
        email: "",
        firstName: personsSettled ? t("Unknown person") : t("Loading…"),
        lastName: "",
      }
  );
  const selectedContracts: MultiSelectOption[] = filters.contractIds.map((id, index) => ({
    value: id,
    label:
      chosenContracts.find((contract) => contract.value === id)?.label ??
      contractLabel(t, contractTitles[index]),
  }));
  const selectedStatuses: MultiSelectOption[] = filters.statuses.map((status) => ({ label: t(status), value: status }));
  const { dueBefore, changedSince } = filters;
  const listQuery = {
    organizationId: authState.selectedOrg.id,
    orgId: authState.selectedOrg.id,
    statuses: filters.statuses.join(","),
    personIds: filters.personIds.join(","),
    contractIds: filters.contractIds.join(","),
    dueBefore,
    changedSince,
  };
  const hasActiveFilters =
    filters.personIds.length > 0 ||
    filters.contractIds.length > 0 ||
    filters.statuses.length > 0 ||
    !!dueBefore ||
    !!changedSince;
  const { currentData: wholeList } = useListContractTasksForOrganizationQuery(
    { organizationId: authState.selectedOrg.id, orgId: authState.selectedOrg.id, limit: 1, withCount: true },
    { skip: !hasActiveFilters },
  );
  const { currentData: filteredList } = useListContractTasksForOrganizationQuery(
    { ...listQuery, limit: 1, withCount: true },
    { skip: !hasActiveFilters },
  );
  const countText = filteredList && wholeList ? taskCountText(t, filteredList, wholeList) : null;

  const filterKey = JSON.stringify(listQuery);
  const firstPage: TaskPaging = { filterKey, positions: [undefined], page: 0 };
  const [storedPaging, setPaging] = useState<TaskPaging>(firstPage);
  const paging = storedPaging.filterKey === filterKey ? storedPaging : firstPage;

  const {
    currentData: tasksForCurrentFilters,
    isError,
    isFetching,
    refetch,
  } = useListContractTasksForOrganizationQuery({
    ...listQuery,
    after: paging.positions[paging.page],
    limit: PAGE_SIZE,
  });
  const nextPosition = tasksForCurrentFilters?.nextPosition ?? undefined;
  const goTo = (skip: number) => {
    const next = skip / PAGE_SIZE;
    setPaging({
      filterKey,
      positions:
        next > paging.page && nextPosition !== undefined
          ? [...paging.positions.slice(0, next), nextPosition]
          : paging.positions,
      page: next,
    });
  };

  const skeletons = useSkeletons().stackedLines;
  const resetFilters = () => updateFilters(NO_TASK_FILTERS);
  const chooseResponsiblePersons = (persons: PersonDto[]) => {
    setChosenPersons((known) => uniqBy([...persons, ...known], (person) => person.id));
    updateFilters({ personIds: persons.map((person) => person.id) });
  };
  const chooseContracts = (contracts: MultiSelectOption[]) => {
    setChosenContracts((known) => uniqBy([...contracts, ...known], (contract) => contract.value));
    updateFilters({ contractIds: contracts.map((contract) => contract.value) });
  };

  const pagingControls = (
    <Paging
      limit={PAGE_SIZE}
      skip={paging.page * PAGE_SIZE}
      setSkip={goTo}
      isLoading={isFetching}
      hasNextPage={nextPosition !== undefined}
    />
  );

  const reports = useListReports({
    list: SavedReportListDto.ContractTasks,
    query: withTaskFilters(searchParams, filters).toString(),
    heading: t("Contract tasks"),
    listFailedToLoad: isError,
    listIsLoading: isFetching,
  });

  return (
    <Grid height="full" gridTemplateColumns="minmax(0, 1fr)" gridTemplateRows="auto 1fr">
      <ListPageHeader
        heading={reports.heading}
        minH="70px"
        py={{ base: "3", sm: "0" }}
        borderBottom="1px solid"
        borderBottomColor="smPageDivider"
        data-testid="contract-task-list-header"
        actions={
          <>
            {reports.actions}
            <TaskListDownloadMenu
              query={listQuery}
              isEmpty={paging.page === 0 && tasksForCurrentFilters?.tasks.length === 0 && nextPosition === undefined}
              isLoading={tasksForCurrentFilters === undefined && !isError}
              failed={isError}
              filters={describeTaskFilters(t, {
                statuses: selectedStatuses,
                responsiblePersons: selectedResponsiblePersons,
                contracts: selectedContracts,
                dueBefore,
                changedSince,
              })}
            />
          </>
        }
      />
      <GridItem>
        {reports.notice}
        <Box mt="15px" mb="20px">
          <Text fontSize="medium" color={"smPageIntroText"} mb="10px">
            {t("Tasks for all contracts, including contracts shared with you")}
          </Text>
        </Box>
        <Box mb="25px">
          <TaskFilterBar
            selectedResponsiblePersons={selectedResponsiblePersons}
            onSelectedResponsiblePersonsChange={chooseResponsiblePersons}
            selectedContracts={selectedContracts}
            onSelectedContractsChange={chooseContracts}
            selectedStatuses={selectedStatuses}
            onSelectedStatusesChange={(statuses) => updateFilters({ statuses: statuses.map((s) => s.value) })}
            extraChips={[
              ...(dueBefore
                ? [{ label: dueBeforeLabel(t, dueBefore), onRemove: () => updateFilters({ dueBefore: undefined }) }]
                : []),
              ...(changedSince
                ? [
                    {
                      label: changedSinceLabel(t, changedSince),
                      onRemove: () => updateFilters({ changedSince: undefined }),
                    },
                  ]
                : []),
            ]}
            onResetAll={resetFilters}
            countText={countText}
          />
          {unknownValues.length > 0 && (
            <UnknownFilterValuesNote values={unknownValues} onDismiss={() => updateFilters({})} />
          )}
        </Box>
        {isError ? (
          <ListLoadError
            message={t("Could not load contract tasks.")}
            onRetry={() => void refetch()}
            reset={hasActiveFilters ? { label: t("Reset table"), onReset: resetFilters } : undefined}
          />
        ) : (
          <>
            {pagingControls}
            {tasksForCurrentFilters ? (
              <TasksTable
                tasks={tasksForCurrentFilters.tasks}
                hasActiveFilters={hasActiveFilters}
                onResetFilters={resetFilters}
                hasMorePages={nextPosition !== undefined}
              />
            ) : (
              skeletons(0, 5)
            )}
            {pagingControls}
          </>
        )}
      </GridItem>
    </Grid>
  );
};

const personFromAddress = (searchParams: URLSearchParams): PersonDto[] => {
  const id = searchParams.get("person");
  const name = searchParams.get("personName");
  if (!id || !name) return [];
  return [{ id, firstName: name, lastName: null, email: "" }];
};

const SHOWN_VALUE_LENGTH = 40;

const UnknownFilterValuesNote = ({
  values,
  onDismiss,
}: {
  values: UnknownTaskFilterValue[];
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Flex align="start" gap="2" mt="3" data-testid="unknown-task-filter-values">
      <Box>
        {values.map((unknown) => (
          <Text key={`${unknown.filter}:${unknown.value}`} fontSize="sm" color="smMuted">
            {unknownValueLine(t, unknown)}
          </Text>
        ))}
      </Box>
      <CloseButton size="sm" aria-label={String(t("Dismiss"))} onClick={onDismiss} />
    </Flex>
  );
};

const unknownValueLine = (t: TFunction, { filter, value }: UnknownTaskFilterValue): string => {
  const shown = shortened(value);
  switch (filter) {
    case "personIds":
      return t("The responsible person «{{value}}» does not exist and is not used in the filter.", { value: shown });
    case "contractIds":
      return t("The contract «{{value}}» does not exist and is not used in the filter.", { value: shown });
    case "statuses":
      return t("The status «{{status}}» does not exist and is not used in the filter.", { status: shown });
  }
};

const shortened = (value: string): string =>
  value.length > SHOWN_VALUE_LENGTH ? `${value.slice(0, SHOWN_VALUE_LENGTH)}…` : value;

const contractLabel = (t: TFunction, title: string | null | undefined): string =>
  title === undefined ? t("Loading…") : title === null ? t("Unknown contract") : title;

const taskCountText = (
  t: TFunction,
  filtered: ListContractTasksResponseDto,
  whole: ListContractTasksResponseDto,
): string | null => {
  if (filtered.count == null || whole.count == null) return null;
  const counts = { shown: formatNumber(filtered.count), total: formatNumber(whole.count) };
  if (filtered.countIsLowerBound) return t("{{shown}}+ of {{total}}+ tasks", counts);
  if (whole.countIsLowerBound) return t("{{shown}} of {{total}}+ tasks", counts);
  return t("{{shown}} of {{count}} tasks", { shown: filtered.count, count: whole.count });
};
